import { Component, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit, NgZone, ChangeDetectorRef, QueryList, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { BrnService, BrnWidgetMessageHistoryResponse } from '../../services/brn.service';
import { FormatMessagePipe } from '../../pipes/format-message.pipe';

interface Message {
  message: {
    type: string;
    content: {
      text?: string;
      [key: string]: unknown;
    };
  };
  author: 'client' | 'bot';
  created: string;
  // UI-only: progressively typed text for bot streaming effect
  displayText?: string;
}

@Component({
  selector: 'app-chat-history',
  standalone: true,
  imports: [CommonModule, FormsModule, FormatMessagePipe],
  templateUrl: './chat-history.component.html',
  styleUrls: ['./chat-history.component.scss']
})
export class ChatHistoryComponent implements OnInit, OnDestroy, AfterViewInit {
  botId: string | null = null;
  userId: string | null = null;
  messages: Message[] = [];
  followUpText = '';
  isLoading = true;
  isSending = false;
  error: string | null = null;
  // WebSocket (Faye) state
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error' = 'disconnected';
  private fayeClient?: any;
  private fayeSubscription?: { cancel?: () => void } | null = null;
  // Typing/streaming state
  isWaitingForBot = false;
  private activeTimers: number[] = [];
  // Track recently sent client messages to suppress websocket echo duplicates
  private pendingClientByText: Map<string, { count: number; firstAt: number }> = new Map();
  private readonly pendingDedupeTtlMs = 15000;
  
  private readonly subscriptions = new Subscription();
  private firstBotReplyHandled = false;
  private firstQuestionCache?: { botId: string; userId: string; clientId: string; question: string; sentAt?: number } | null = null;
  // perf markers
  private navStartPerf = 0;
  private wsUpPerf = 0;
  private firstBotPerf = 0;

  @ViewChild('messagesEnd') private messagesEndRef?: ElementRef<HTMLDivElement>;
  @ViewChildren('messageItem') private messageItems?: QueryList<ElementRef<HTMLDivElement>>;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly brnService: BrnService,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.navStartPerf = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
    this.botId = this.route.snapshot.paramMap.get('bot_id');
    this.userId = this.route.snapshot.paramMap.get('user_id');
    console.log('[History] init', { botId: this.botId, userId: this.userId });
    // If we have a cached first question from send page, show typing immediately
    const cached = this.readFirstQuestion();
    if (cached) {
      this.isWaitingForBot = true;
      console.log('[History] first question present in storage; show typing', { cached });
    }
    
    if (!this.botId || !this.userId) {
      this.error = 'Missing bot_id or user_id in URL';
      this.isLoading = false;
      return;
    }

    this.loadMessageHistory();
    // Initialize realtime subscription
    this.initRealtime();
  }

  ngAfterViewInit(): void {
    // Scroll to absolute bottom after view init
    this.deferScrollToBottom();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // Clean up websocket subscription/client
    try { this.fayeSubscription?.cancel?.(); } catch {}
    try { (this.fayeClient as any)?.disconnect?.(); } catch {}
    this.fayeSubscription = null;
    this.fayeClient = undefined;
    // Clear typing timers
    try { this.activeTimers.forEach(id => window.clearInterval(id)); } catch {}
    this.activeTimers = [];
  }

  private async loadMessageHistory(): Promise<void> {
    if (!this.botId || !this.userId) return;

    try {
      console.log('[History] loadMessageHistory start');
      this.isLoading = true;
      const response = await firstValueFrom(
        this.brnService.getWidgetMessageHistory(this.botId, this.userId)
      );
      
      if (response.status && response.messages) {
        console.log('[History] message history loaded', { count: response.messages.length });
        // Normalize messages to ensure text property exists
        this.messages = response.messages.map(msg => ({
          ...msg,
          message: {
            ...msg.message,
            content: {
              ...msg.message.content,
              text: (msg.message.content['text'] as string) || ''
            }
          },
          displayText: (msg.message.content['text'] as string) || ''
        }));
        // If the latest message is a client message, we're waiting for the bot
        const last = this.messages[this.messages.length - 1];
        if (last?.author === 'client') {
          this.isWaitingForBot = true;
          console.log('[History] last message from client -> set isWaitingForBot=true');
        }
      }
    } catch (error) {
      console.error('[History] Failed to load message history:', error);
      this.error = 'Failed to load message history';
    } finally {
      this.isLoading = false;
      // Ensure we scroll to the absolute bottom after loading and DOM update
      this.deferScrollToBottom();
      console.log('[History] loadMessageHistory finished');
    }
  }

  async sendFollowUp(): Promise<void> {
    const text = this.followUpText.trim();
    if (!text || this.isSending || !this.botId || !this.userId) {
      return;
    }

    this.isSending = true;
    this.isWaitingForBot = true;
    console.log('[History] sendFollowUp start', { text });

    try {
      // Optimistically render the user's message
      const newMessage: Message = {
        message: {
          type: 'text',
          content: { text }
        },
        author: 'client',
        created: new Date().toISOString(),
        displayText: text
      };
      this.messages = [...this.messages, newMessage];
      this.deferScrollToBottom();
      // Mark as pending BEFORE network call to catch fast websocket echo
      this.addPendingClientText(text);

      await firstValueFrom(
        this.brnService.sendWidgetMessage({
          bot_id: this.botId,
          user_id: this.userId,
          type: 'text',
          content: { text }
        })
      );

      this.followUpText = '';
      console.log('[History] sendFollowUp message sent');
    } catch (error) {
      console.error('[History] Failed to send follow-up message:', error);
      // If sending failed, remove the optimistic message and indicator
      this.messages.pop();
      this.isWaitingForBot = false;
      // Undo pending mark on failure
      this.consumePendingClientText(text, false);
    } finally {
      this.isSending = false;
      console.log('[History] sendFollowUp finished');
    }
  }

  formatMessageTime(created: string): string {
    const date = new Date(created);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  }

  isRecentMessage(created: string): boolean {
    const messageTime = new Date(created);
    const now = new Date();
    const diffMinutes = (now.getTime() - messageTime.getTime()) / (1000 * 60);
    return diffMinutes < 5; // Consider messages from last 5 minutes as recent
  }

  trackByMessage(index: number, message: Message): string {
    return message.created + message.author + (message.message.content.text || '');
  }

  private scrollToBottom(): void {
    try {
      // Scroll to the absolute maximum possible bottom of the page
      const maxHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.offsetHeight,
        document.body.clientHeight,
        document.documentElement.clientHeight
      );
      const targetTop = Math.max(0, maxHeight - window.innerHeight + 2);
      window.scrollTo({ top: targetTop, behavior: 'auto' });
    } catch {}
  }

  private deferScrollToBottom(): void {
    // Ensure change detection runs and the view is stable before scrolling
    this.cdr.detectChanges();
    this.zone.runOutsideAngular(() => {
      // Use a few animation frames to ensure layout is fully settled before jumping
      const run = (tries: number) => {
        this.scrollToBottom();
        if (tries > 0) requestAnimationFrame(() => run(tries - 1));
      };
      requestAnimationFrame(() => run(3));
    });
    console.log('[History] scrolled to bottom');
  }

  // Realtime (Faye) helpers
  private async initRealtime(): Promise<void> {
    if (!this.userId) return;
    try {
      await this.loadFayeScript();
    } catch (e) {
      console.error('Failed to load Faye client:', e);
      this.connectionStatus = 'error';
      return;
    }

    const FayeCtor = (window as any).Faye?.Client;
    if (!FayeCtor) {
      this.connectionStatus = 'error';
      console.error('Faye client not available on window');
      return;
    }

    // Create client and subscribe to user-specific channel
    try {
      this.connectionStatus = 'connecting';
      this.fayeClient = new FayeCtor('https://faye.brn.ai/faye');
      console.log('[History] Faye client created');

      // Bind connection indicators if available
      const clientAny = this.fayeClient as any;
      const bind = typeof clientAny?.bind === 'function' ? clientAny.bind.bind(clientAny) : null;
      const on = typeof clientAny?.on === 'function' ? clientAny.on.bind(clientAny) : null;
      const attach = (event: string, cb: () => void) => {
        try {
          if (bind) bind(event, () => this.zone.run(() => cb()));
          else if (on) on(event, () => this.zone.run(() => cb()));
        } catch {}
      };
      attach('transport:up', () => {
        this.connectionStatus = 'connected';
        this.wsUpPerf = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
        const wsConnectMs = this.wsUpPerf && this.navStartPerf ? Math.round(this.wsUpPerf - this.navStartPerf) : undefined;
        console.log('[History] Faye transport up', { wsConnectMs });
      });
      attach('transport:down', () => {
        this.connectionStatus = 'disconnected';
        console.log('[History] Faye transport down');
      });

      const channel = `/messages_${this.userId}`;
      this.fayeSubscription = this.fayeClient.subscribe(channel, (payload: any) => {
        console.log('[History] Faye message received', payload);
        this.zone.run(() => {
          const normalized = this.normalizeIncomingMessage(payload);
          // Start streaming animation for bot responses
          if (normalized.author === 'bot') {
            this.isWaitingForBot = false;
            this.firstBotPerf = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
            const sentAt = this.firstQuestionCache?.sentAt;
            const ttfbMs = sentAt ? Math.max(0, Date.now() - sentAt) : undefined;
            const firstTokenFromNavMs = this.firstBotPerf && this.navStartPerf ? Math.round(this.firstBotPerf - this.navStartPerf) : undefined;
            // Break down server vs transit: use payload.created as server timestamp
            const createdIso = (payload?.created || payload?.message?.created) as string | undefined;
            const createdTs = createdIso ? Date.parse(createdIso) : undefined;
            const serverProcessingMs = createdTs && sentAt ? Math.max(0, createdTs - sentAt) : undefined;
            const networkTransitMs = createdTs ? Math.max(0, Date.now() - createdTs) : undefined;
            console.log('[History] bot message received, stop waiting', { ttfbMs, firstTokenFromNavMs, serverProcessingMs, networkTransitMs, createdIso });
            normalized.displayText = '';
            this.messages = [...this.messages, normalized];
            this.startTypingAnimation(normalized);
            // Trigger subject detection strictly after first bot reply arrives
            if (!this.firstBotReplyHandled) {
              this.firstBotReplyHandled = true;
              const firstQuestion = this.readFirstQuestion() || this.messages.find(m => m.author === 'client')?.message?.content?.text || '';
              if (firstQuestion && this.botId && this.userId) {
                // Fire-and-forget; no await
                setTimeout(() => {
                  this.detectAndStoreSubject(this.botId!, this.userId!, firstQuestion).catch(err =>
                    console.warn('Subject detection failed', err)
                  );
                }, 0);
              }
            }
          } else {
            // Suppress duplicate echo if we just sent the same text
            const text = normalized.message.content.text || '';
            if (this.consumePendingClientText(text, true)) return;
            normalized.displayText = text;
            this.messages = [...this.messages, normalized];
            this.deferScrollToBottom();
          }
        });
      });

      // Consider connected once subscription object exists
      this.connectionStatus = 'connected';
      console.log('[History] Faye subscribed to channel', channel);
    } catch (err) {
      console.error('[History] Failed to initialize realtime subscription:', err);
      this.connectionStatus = 'error';
    }
  }

  private readFirstQuestion(): string | null {
    try {
      const key = this.botId && this.userId ? `stitch:first-question:${this.botId}:${this.userId}` : null;
      if (!key) return null;
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.question === 'string') {
        this.firstQuestionCache = parsed;
        // Clean up after consuming
        sessionStorage.removeItem(key);
        if (parsed.sentAt) {
          console.log('[History] first question latency ms', Date.now() - parsed.sentAt);
        }
        return parsed.question;
      }
      return null;
    } catch {
      return null;
    }
  }

  private async detectAndStoreSubject(botId: string, userId: string, firstQuestion: string): Promise<void> {
    try {
      // Find installed service for response_generation
      const servicesResp = await firstValueFrom(
        this.brnService.getServicesByCapability(botId, 'response_generation')
      );
      const installed = servicesResp?.services?.find(s => !!s.installed);
      if (!installed) return;

      const subjectResp = await firstValueFrom(
        this.brnService.generateServiceResponse(botId, installed.name, firstQuestion)
      );
      const subject =
        (subjectResp as any)?.text ||
        (subjectResp as any)?.output ||
        (subjectResp as any)?.message ||
        (typeof (subjectResp as any)?.response === 'string' ? (subjectResp as any).response : undefined);
      if (!subject) return;

      // Load client by client_id (we have userId, need clientId -> search clients list)
      // Prefer cached clientId from chat component storage to avoid extra list request
      let clientId: string | undefined = this.firstQuestionCache?.clientId;
      if (!clientId) {
        const clients = await firstValueFrom(this.brnService.getWidgetClients(botId));
        const target = clients.find(c => {
          const providerData = (c.provider_data as any) || {};
          const pdUserId = providerData?.data?.user_id || providerData?.user_id;
          return pdUserId === userId;
        });
        clientId = target?.client_id;
      }
      if (!clientId) return;

      await firstValueFrom(this.brnService.updateClient(clientId, botId, subject));
      console.log('Updated client name with CHAT SUBJECT (history hook)');
    } catch (err) {
      console.warn('detectAndStoreSubject error', err);
    }
  }

  private loadFayeScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).Faye?.Client) {
        resolve();
        return;
      }
      const existing = document.getElementById('faye-client-script');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Faye script failed to load')));
        return;
      }
      const script = document.createElement('script');
      script.id = 'faye-client-script';
      script.type = 'text/javascript';
      script.src = 'https://faye.brn.ai/faye/client.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Faye script failed to load'));
      document.head.appendChild(script);
    });
  }

  private normalizeIncomingMessage(payload: any): Message {
    const type = payload?.message?.type ?? payload?.type ?? 'text';
    const text =
      payload?.message?.content?.text ??
      payload?.content?.text ??
      payload?.text ??
      '';
    const content: Record<string, unknown> = {
      ...(payload?.message?.content ?? payload?.content ?? {}),
      text
    };
    const author: 'client' | 'bot' = payload?.author === 'client' ? 'client' : 'bot';
    const created: string = payload?.created ?? new Date().toISOString();
    return {
      message: { type, content },
      author,
      created,
      displayText: text
    };
  }

  private startTypingAnimation(targetMessage: Message): void {
    const fullText = targetMessage?.message?.content?.text || '';
    if (!fullText) {
      targetMessage.displayText = '';
      this.deferScrollToBottom();
      return;
    }

    // Aim for ~60 frames total regardless of length; cap duration ~1.2s
    const maxFrames = 60;
    const intervalMs = 16; // ~60fps
    const step = Math.max(1, Math.ceil(fullText.length / maxFrames));
    let i = 0;
    const id = window.setInterval(() => {
      i = Math.min(fullText.length, i + step);
      targetMessage.displayText = fullText.slice(0, i);
      this.cdr.detectChanges();
      this.deferScrollToBottom();
      if (i >= fullText.length) {
        window.clearInterval(id);
        const renderDonePerf = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
        const renderFromFirstTokenMs = this.firstBotPerf && renderDonePerf ? Math.round(renderDonePerf - this.firstBotPerf) : undefined;
        const totalFromSendMs = this.firstQuestionCache?.sentAt ? Math.max(0, Date.now() - this.firstQuestionCache.sentAt) : undefined;
        console.log('[History] typing animation complete', { renderFromFirstTokenMs, totalFromSendMs });
      }
    }, intervalMs);
    this.activeTimers.push(id);
  }

  private addPendingClientText(text: string): void {
    const now = Date.now();
    const existing = this.pendingClientByText.get(text);
    if (existing) {
      existing.count += 1;
    } else {
      this.pendingClientByText.set(text, { count: 1, firstAt: now });
    }
    this.pruneOldPending(now);
  }

  private consumePendingClientText(text: string, checkTtl = true): boolean {
    const entry = this.pendingClientByText.get(text);
    if (!entry) return false;
    const now = Date.now();
    if (checkTtl && now - entry.firstAt > this.pendingDedupeTtlMs) {
      this.pendingClientByText.delete(text);
      return false;
    }
    entry.count -= 1;
    if (entry.count <= 0) this.pendingClientByText.delete(text);
    return true;
  }

  private pruneOldPending(now = Date.now()): void {
    try {
      this.pendingClientByText.forEach((entry, key) => {
        if (now - entry.firstAt > this.pendingDedupeTtlMs) {
          this.pendingClientByText.delete(key);
        }
      });
    } catch {}
  }
}
